import { useFormik } from 'formik';
import * as Yup from 'yup';
import { ApiError } from '@/services/api/client';
import { useVerifyMfa } from '@/services/queries';
import type { MfaChallenge } from '@/types';
import { Button } from '@/shared/ui/button';
import { BackLink, CodeInput, FormError } from './fields';
import { SUBMIT, SUBTITLE, TITLE } from './styles';

/**
 * The second half of a login, laid out like the admin console's MFA step.
 *
 * Shown once the password has been accepted and the server has answered with a
 * challenge. Nothing is stored at that point - the challenge grants nothing on
 * its own, and presenting it without a correct code gets exactly as far as
 * presenting nothing.
 *
 * The challenge is single-use and is spent whether or not the code is right, so
 * a wrong code means starting the login again. That is deliberate on the server
 * - it stops somebody with a stolen password grinding six digits against one
 * challenge - and it is why the failure message here says to sign in again
 * rather than inviting another guess at the same prompt.
 */
const schema = Yup.object({
  code: Yup.string()
    .trim()
    .required('Enter the code from your authenticator app.')
    // As a string, not a number: 012345 parsed as a number is 12345, which
    // would never match.
    .matches(/^\d{6}$/, 'The code is six digits.'),
});

export function MfaChallengeForm({
  challenge,
  onStartOver,
}: {
  challenge: MfaChallenge;
  /** Back to the password form, since a spent challenge cannot be retried. */
  onStartOver: () => void;
}) {
  const verify = useVerifyMfa();

  const formik = useFormik({
    initialValues: { code: '' },
    validationSchema: schema,
    onSubmit: (values) => {
      verify.mutate({ challengeId: challenge.challengeId, code: values.code.trim() });
    },
  });

  const apiError = verify.error instanceof ApiError ? verify.error : null;
  const error = formik.touched.code ? formik.errors.code : undefined;

  return (
    <form onSubmit={formik.handleSubmit} className="space-y-4" noValidate>
      <header>
        <h1 className={TITLE}>Two-factor verification</h1>
        <p className={SUBTITLE}>
          Enter the 6-digit code from your authenticator app. Codes rotate every 30 seconds.
        </p>
      </header>

      <FormError
        message={apiError ? `${apiError.message} Sign in again to get a new prompt.` : null}
        {...(apiError?.requestId ? { requestId: apiError.requestId } : {})}
      />

      <CodeInput
        id="code"
        label="Verification code"
        autoFocus
        disabled={verify.isPending}
        value={formik.values.code}
        onChange={(value) => void formik.setFieldValue('code', value)}
        onBlur={() => void formik.setFieldTouched('code', true)}
        {...(error ? { error } : {})}
      />

      <Button
        type="submit"
        className={SUBMIT}
        disabled={verify.isPending || formik.values.code.length !== 6}
      >
        {verify.isPending ? 'Verifying…' : 'Verify and continue'}
      </Button>

      {/* The challenge is spent on the server whatever the outcome, so there
          is nothing to retry here - only a fresh login. */}
      <BackLink label="Back to sign in" onClick={onStartOver} />
    </form>
  );
}
